import {
  AppstoreAddOutlined,
  BoldOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  MenuOutlined,
  SaveOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { Input } from '@app/components/common/inputs/Input/Input';
import { dealRoomSections } from '@app/constants/dealRoom';
import {
  IDealRoomComparisonSection,
  IDealRoomMenuSection,
  IDealRoomOfferSection,
  IDealRoomSection,
  IDealRoomSectionContent,
} from '@app/interfaces/deal-room';
import editImg from 'assets/images/edit.svg';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './DealRoomMenu.styles';
import { Badge, Tooltip } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { BREAKPOINTS } from '@app/styles/themes/constants';
import { getDisplayMenuSections } from '@app/utils/dealRoom';
import { DescriptionText } from '@app/components/common/DescriptionText/DescriptionText';
import { DealRoomSection } from '@app/constants/enums/deal-room';

interface DealRoomMenuProps {
  sections: IDealRoomSection[];
  offerSections: IDealRoomOfferSection[];
  menuSections: IDealRoomMenuSection[];
  comparisonSections: IDealRoomComparisonSection[];
  isAdmin: boolean;
  unreadFiles: number;
  onOpenMenuModal: (open: boolean) => void;
  onUpdateSections: (sections: typeof dealRoomSections) => void;
}

export const DealRoomMenu: React.FC<DealRoomMenuProps> = ({
  sections,
  offerSections,
  menuSections,
  comparisonSections,
  isAdmin,
  unreadFiles,
  onOpenMenuModal,
  onUpdateSections,
}) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<string | string[]>([]);
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.sm });
  const isOpen = activeKey.length > 0;

  const [hovered, setHovered] = useState<string | null>(null);

  const [items, setItems] = useState<typeof dealRoomSections>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [value, setValue] = useState<string>('');

  const cleanUrl = window.location.href.split('#')[0];

  const updateItems = (newItems: typeof dealRoomSections) => {
    const allSectionExceptCategory = newItems.filter((item) => !item.isCategory);
    const category = newItems.filter((item) => item.isCategory);
    const allSectionInsideCategory = newItems.filter((item) => item.isCategory).flatMap((i) => i.subMenu || []);

    const fullSections = allSectionExceptCategory.concat(category).concat(allSectionInsideCategory);
    onUpdateSections(fullSections);
  };

  const toggleIcon = (key: string, type: 'highlighted' | 'display' | 'default' | 'isEditing' | 'delete') => {
    if (type === 'delete') {
      const newItems = items
        .filter((item) => item.key !== key)
        .map((item) => {
          if (item.subMenu && item.subMenu.length > 0) {
            return {
              ...item,
              subMenu: item.subMenu.filter((subItem) => subItem.key !== key),
            };
          }
          return item;
        });
      setItems(newItems);
      updateItems(newItems);
      return;
    }

    const isDefault = type === 'default';
    /**
     * Only one section may be the default, so starring one clears every other.
     * A parent is cleared like anything else: while this was an `else if` chain
     * a parent holding a subMenu never reached the clearing branch and kept its
     * star, and the landing — which looks at top-level sections before their
     * children — then opened on that parent rather than the section starred.
     */
    const clearIfDefault = (entry: IDealRoomSectionContent): IDealRoomSectionContent =>
      isDefault && entry.key !== key && entry.default ? { ...entry, default: false } : entry;

    const newItems = items.map((item) => {
      const next = item.key === key ? { ...item, [type]: !item[type] } : clearIfDefault(item);
      if (!item.subMenu || item.subMenu.length === 0) {
        return next;
      }
      return {
        ...next,
        subMenu: item.subMenu.map((subItem) =>
          subItem.key === key ? { ...subItem, [type]: !subItem[type] } : clearIfDefault(subItem),
        ),
      };
    });

    setItems(newItems);
    if (type !== 'isEditing') {
      updateItems(newItems);
    } else {
      const currentItem =
        newItems.find((item) => item.key === key) ||
        newItems.flatMap((i) => i.subMenu || []).find((sub) => sub.key === key);
      setValue(currentItem?.label || t(currentItem?.labelKey || ''));
      setSelectedKey(key);
    }
  };

  const saveLabel = () => {
    if (!selectedKey) return;

    const newItems = items.map((item) => {
      if (item.key === selectedKey) {
        return {
          ...item,
          label: value,
          isEditing: false,
        };
      } else if (item.subMenu && item.subMenu.length > 0) {
        return {
          ...item,
          subMenu: item.subMenu.map((subItem) => {
            if (subItem.key === selectedKey) {
              return {
                ...subItem,
                label: value,
                isEditing: false,
              };
            }
            return subItem;
          }),
        };
      }

      return item;
    });

    setItems(newItems);
    updateItems(newItems);
    setSelectedKey(null);
    setValue('');
  };

  useEffect(() => {
    setItems(getDisplayMenuSections(sections, offerSections, menuSections, isAdmin, comparisonSections, t));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, offerSections, menuSections, comparisonSections]);

  useEffect(() => {
    if (isMobile) {
      setActiveKey([]);
    } else {
      setActiveKey(['1']);
    }
  }, [isMobile]);

  const itemLabel = (item: IDealRoomSectionContent) => {
    const label = item.label ? item.label : item.isExternal ? item.labelKey : t(item.labelKey);
    const files = item.key === DealRoomSection.ATTACHMENTS && unreadFiles > 0;
    return (
      <S.LabelWrapper>
        <Badge dot={files} offset={[-5, 0]} size="default">
          <S.Link href={cleanUrl + '#' + item.key}>
            {label} {item.default && isAdmin && hovered !== item.key && <StarOutlined className="orange" />}
          </S.Link>
        </Badge>
        {item.isExternal && item.description && <DescriptionText description={item.description} title={label} />}
      </S.LabelWrapper>
    );
  };

  const renderMenu = (arr: typeof dealRoomSections, isSubMenu = false) => (
    <S.MenuList>
      {arr.map((item) => (
        <React.Fragment key={item.key}>
          <Tooltip title={item?.tooltip || null}>
            <S.MenuItem
              $hovered={hovered === item.key}
              className={`${hovered === item.key ? 'hovered' : ''} ${item.highlighted ? 'bold' : ''} ${
                !item.display ? 'eye' : ''
              } ${isSubMenu ? 'sub-menu-item' : ''}`}
              onMouseEnter={() => setHovered(item.key)}
              onMouseLeave={() => setHovered(null)}
            >
              {item.isEditing ? <Input value={value} onChange={(e) => setValue(e.target.value)} /> : itemLabel(item)}
              {hovered === item.key && isAdmin && (
                <S.HoveredIcons>
                  {item.isEditing ? (
                    <Tooltip title={t('dealRooms.landing.updateSection')}>
                      <SaveOutlined onClick={() => saveLabel()} />
                    </Tooltip>
                  ) : (
                    <>
                      {(item.isAdditional || item.dealRoomMenu) && (
                        <Tooltip title={t('dealRooms.landing.deleteSection')}>
                          <DeleteOutlined onClick={() => toggleIcon(item.key, 'delete')} />
                        </Tooltip>
                      )}
                      <Tooltip title={t('dealRooms.landing.defaultSection')}>
                        {item.default ? (
                          <StarOutlined className="orange" onClick={() => toggleIcon(item.key, 'default')} />
                        ) : (
                          <StarOutlined onClick={() => toggleIcon(item.key, 'default')} />
                        )}
                      </Tooltip>
                      <Tooltip title={t('dealRooms.landing.editSection')}>
                        <img src={editImg} alt="Edit" onClick={() => toggleIcon(item.key, 'isEditing')} />
                      </Tooltip>
                      <Tooltip
                        title={t(
                          item.highlighted
                            ? 'dealRooms.landing.unhighlightSection'
                            : 'dealRooms.landing.highlightSection',
                        )}
                      >
                        <BoldOutlined onClick={() => toggleIcon(item.key, 'highlighted')} />
                      </Tooltip>
                      <Tooltip
                        title={t(item.display ? 'dealRooms.landing.hideSection' : 'dealRooms.landing.displaySection')}
                      >
                        {item.display ? (
                          <EyeOutlined onClick={() => toggleIcon(item.key, 'display')} />
                        ) : (
                          <EyeInvisibleOutlined onClick={() => toggleIcon(item.key, 'display')} />
                        )}
                      </Tooltip>
                    </>
                  )}
                </S.HoveredIcons>
              )}
            </S.MenuItem>
          </Tooltip>

          {item.subMenu && item.subMenu.length > 0 && (
            <S.SubMenuWrapper>{renderMenu(item.subMenu, true)}</S.SubMenuWrapper>
          )}
        </React.Fragment>
      ))}
    </S.MenuList>
  );

  return (
    <S.StyledCollapse ghost activeKey={activeKey} onChange={(key) => setActiveKey(key)}>
      <S.Panel
        header={
          <S.HeaderContainer>
            <S.HeaderWrapper>
              <S.BlueButton className="rounded">
                <MenuOutlined /> {t('dealRooms.landing.tableOfContents')}
                {isOpen ? (
                  <Tooltip title={t('dealRooms.landing.hideMenu')}>
                    <EyeOutlined onClick={() => setActiveKey([])} />
                  </Tooltip>
                ) : (
                  <Tooltip title={t('dealRooms.landing.displayMenu')}>
                    <EyeInvisibleOutlined onClick={() => setActiveKey(['1'])} />
                  </Tooltip>
                )}
              </S.BlueButton>
            </S.HeaderWrapper>
            {isAdmin && (
              <S.HeaderWrapper>
                <Tooltip title={t('dealRooms.landing.addUncommonSections')}>
                  <S.BlueButton
                    className="rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenMenuModal(true);
                    }}
                  >
                    <AppstoreAddOutlined /> {t('dealRooms.landing.addSection')}
                  </S.BlueButton>
                </Tooltip>
              </S.HeaderWrapper>
            )}
          </S.HeaderContainer>
        }
        key="1"
      >
        {renderMenu(items)}
      </S.Panel>
    </S.StyledCollapse>
  );
};
